import {z} from "zod/v4-mini"
import type {Evaluator} from "@cm/graph-v3"
import {graphNode} from "@cm/graph-v3"
import {registerTemplateGraphNode, getTemplateNodeReturns, externalId, NotReady, WaitReady, visitNone} from "@cm/template-nodes"
import type {
    OnCompileContext,
    TemplateNodeContext,
    TemplateNodeDefinition,
    VisitorNodeResult,
    ExternalId,
    BuilderInlet,
    BuilderOutlet,
    GraphBuilderScope,
    NodeEvaluator,
    TemplateNode,
} from "@cm/template-nodes"
import {MattressNode} from "#mattress/nodes/mattress-node"
import {MattressCurve} from "#mattress/nodes/curve/mattress-curve"
import type {MattressCurveData} from "#mattress/nodes/curve/mattress-curve"
import type {
    EvaluatedMattressComponentInput,
    EvaluatedMattressComponentInputs,
    EvaluatedMattressComponentValue,
    EvaluatedMattressComponentValueOrigin,
    EvaluatedMattressComponentValueType,
} from "#mattress/nodes/component/mattress-component-types"
import {getMattressCompileContext} from "#mattress/nodes/component/mattress-component-helpers"
import {mattressCurveLikeMarker} from "#mattress/role-markers"
import type {MattressCurveLikeNode} from "#mattress/role-markers"
import {MattressCurveInfo} from "#mattress/nodes/component/mattress-component-interface-descriptors"

type ResolvedEvaluatedMattressComponentInput<V extends EvaluatedMattressComponentValue, O extends EvaluatedMattressComponentValueOrigin> = Omit<
    EvaluatedMattressComponentInput<EvaluatedMattressComponentValueType, O, V | null>,
    "type"
>

// Casts: `EvaluatedMattressComponentInput` is not a discriminated union — its `value`/`origin`
// types aren't tied to `type` at the type level. The caller passes matching V/O generics for the
// given type tag; runtime equality on `type` is checked, then the value/origin are trusted.
const resolveComponentInput = <
    T extends EvaluatedMattressComponentValueType,
    O extends EvaluatedMattressComponentValueOrigin,
    V extends EvaluatedMattressComponentValue,
>(
    entry: EvaluatedMattressComponentInput,
    type: T,
    externalId: string,
): ResolvedEvaluatedMattressComponentInput<V, O> => {
    if (entry.type === type) {
        return {value: entry.value as V, origin: entry.origin as O}
    } else throw Error(`Type mismatch for mattress component input ${externalId}: expected ${type}, got ${entry.type}`)
}

const getComponentInput = <
    T extends EvaluatedMattressComponentValueType,
    O extends EvaluatedMattressComponentValueOrigin,
    V extends EvaluatedMattressComponentValue,
>(
    node: TemplateNode,
    scope: GraphBuilderScope,
    componentInputs: EvaluatedMattressComponentInputs,
    externalId: string,
    type: T,
): BuilderInlet<ResolvedEvaluatedMattressComponentInput<V, O>> | undefined => {
    const entry = componentInputs[externalId]

    if (entry === undefined) return undefined

    if (entry !== NotReady) {
        return resolveComponentInput<T, O, V>(entry, type, externalId)
    }

    const waitReady = scope.node(WaitReady<EvaluatedMattressComponentInput>, {
        input: entry,
    })

    return scope.pureLambda(
        scope.tuple(waitReady.output as BuilderOutlet<EvaluatedMattressComponentInput>, type, externalId),
        ([entry, type, externalId]) => resolveComponentInput<T, O, V>(entry, type, externalId),
        `componentInput-${node.instanceId}`,
    )
}

export type MattressCurveInputParameters = {
    name: string
    id: ExternalId
    default?: MattressCurve
}

const definition: TemplateNodeDefinition<MattressCurveInputParameters> = {
    nodeClass: "MattressCurveInput",
    validation: {
        parameters: z.object({
            name: z.string(),
            id: externalId,
            default: z.optional(graphNode(MattressCurve)),
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class MattressCurveInput extends MattressNode<MattressCurveInputParameters> implements MattressCurveLikeNode {
    get [mattressCurveLikeMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<MattressCurveInputParameters> {
        const {id, name, default: defaultValue} = this.parameters
        const mattressContext = getMattressCompileContext(context)
        const {evaluator} = context
        const {templateScope} = evaluator
        const {componentInputs, evaluatedData} = mattressContext
        const {componentsDescriptorLists} = evaluatedData

        const scope = evaluator.getScope(this)

        const defaultInputData = scope.struct<ResolvedEvaluatedMattressComponentInput<MattressCurveData, MattressCurveLikeNode>>("MattressCurveInputData", {
            value: defaultValue ? evaluator.evaluate(defaultValue) : null,
            origin: defaultValue,
        })
        const inputData =
            getComponentInput<"mattressCurve", MattressCurveLikeNode, MattressCurveData>(this, templateScope, componentInputs, id, "mattressCurve") ??
            defaultInputData

        componentsDescriptorLists.push(
            scope.pureLambda(
                scope.tuple(id, name, inputData),
                ([id, name, {value, origin}]) => [
                    new MattressCurveInfo({
                        id,
                        name,
                        value,
                        type: "input",
                        origin,
                    }),
                ],
                "MattressCurveInfo",
            ),
        )

        templateScope.alias(scope.get(inputData, "value"), `mattressComponentInputs-${evaluator.getLocalId(this)}`)
        return visitNone(this.parameters)
    }

    evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderOutlet<MattressCurveData | null> {
        const {templateScope} = evaluator
        return scope.unresolvedToNull(templateScope.resolve<MattressCurveData | null>(`mattressComponentInputs-${evaluator.getLocalId(this)}`))
    }
}
